import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building, CreditCard, FileText, CheckCircle2, Edit2, ShieldCheck } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorKYCEditScreen() {
  const {
    goBack
  } = useNavigation();
  const {
    store
  } = useVendor();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      goBack();
    }, 2000);
  };
  if (isSubmitted) {
    return <div className="min-h-full bg-white dark:bg-gray-900 flex flex-col items-center justify-center p-6" data-id="element-1897">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6" data-id="element-1898">
          <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" data-id="element-1899" />
        </motion.div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1900">
          KYC Updated
        </h2>
        <p className="text-center text-gray-500 dark:text-gray-400" data-id="element-1901">
          Your KYC information has been successfully updated.
        </p>
      </div>;
  }
  return <div className="min-h-full bg-gray-50 dark:bg-gray-900 pb-10" data-id="element-1902">
      <Header title="Edit KYC" showBack data-id="element-1903" />

      <div className="bg-green-50 dark:bg-green-900/20 p-4 m-4 rounded-xl flex items-start gap-3 border border-green-100 dark:border-green-800" data-id="element-1904">
        <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" data-id="element-1905" />
        <p className="text-sm text-green-800 dark:text-green-300" data-id="element-1906">
          Your account is verified. Some fields are locked and cannot be changed
          without contacting support.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-6 pt-0" data-id="element-1907">
        {/* Business Info */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-1908">
          <div className="flex items-center justify-between mb-4" data-id="element-1909">
            <div className="flex items-center gap-2" data-id="element-1910">
              <Building className="w-5 h-5 text-primary" data-id="element-1911" />
              <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1912">
                Business Information
              </h3>
            </div>
          </div>
          <div className="space-y-4" data-id="element-1913">
            <div data-id="element-1914">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1915">
                Business Name
              </label>
              <div className="relative" data-id="element-1916">
                <input type="text" readOnly defaultValue={store.businessInfo.businessName} className="w-full p-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-300 pr-10" data-id="element-1917" />
                <CheckCircle2 className="w-5 h-5 text-green-500 absolute right-3 top-1/2 -translate-y-1/2" data-id="element-1918" />
              </div>
            </div>
            <div data-id="element-1919">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1920">
                CAC Registration Number
              </label>
              <div className="relative" data-id="element-1921">
                <input type="text" readOnly defaultValue={store.businessInfo.taxId} className="w-full p-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-300 pr-10" data-id="element-1922" />
                <CheckCircle2 className="w-5 h-5 text-green-500 absolute right-3 top-1/2 -translate-y-1/2" data-id="element-1923" />
              </div>
            </div>
            <div data-id="element-1924">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1925">
                Business Address
              </label>
              <textarea rows={2} defaultValue={store.businessInfo.businessAddress} className="w-full p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1926"></textarea>
            </div>
          </div>
        </div>

        {/* Bank Info */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-1927">
          <div className="flex items-center gap-2 mb-4" data-id="element-1928">
            <CreditCard className="w-5 h-5 text-primary" data-id="element-1929" />
            <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1930">
              Bank Information
            </h3>
          </div>
          <div className="space-y-4" data-id="element-1931">
            <div data-id="element-1932">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1933">
                Bank Name
              </label>
              <select defaultValue={store.bankDetails?.bankName} className="w-full p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1934">
                <option data-id="element-1935">First Bank of Nigeria</option>
                <option data-id="element-1936">Guaranty Trust Bank</option>
                <option data-id="element-1937">Zenith Bank</option>
                <option data-id="element-1938">Access Bank</option>
              </select>
            </div>
            <div data-id="element-1939">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1940">
                Account Number
              </label>
              <input type="text" defaultValue={store.bankDetails?.accountNumber} className="w-full p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1941" />
            </div>
            <div data-id="element-1942">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1943">
                Account Name
              </label>
              <div className="relative" data-id="element-1944">
                <input type="text" readOnly defaultValue={store.bankDetails?.accountName} className="w-full p-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-300 pr-10" data-id="element-1945" />
                <CheckCircle2 className="w-5 h-5 text-green-500 absolute right-3 top-1/2 -translate-y-1/2" data-id="element-1946" />
              </div>
            </div>
          </div>
        </div>

        <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-xl font-semibold" data-id="element-1947">
          Save Changes
        </Button>
      </form>
    </div>;
}